import { CallButton } from 'components/topics/detail/components/call_button'
import { useMediaQuery } from 'hooks/use_media_query'
import React from 'react'

import { MEDIA_QUERIES, css } from 'design'
import { Button } from 'primitives/button'
import { Stack } from 'primitives/containers'
import { cn } from 'util/cn'

import { useCallManager } from '../../../contexts/call_manager_context'
import { useParticipantManager } from '../../../contexts/participant_manager_context'
import { AppErrorMessage } from '../../../controls/errors/app_error_message'
import { useSetError } from '../../../hooks/use_set_error'
import {
  ScreenOffIcon,
  ScreenOnIcon,
  VideoOffIcon,
  VideoOnIcon,
} from '../../../images'
import { isLiveChatApp } from '../../../reduxes/slices/live_chat_app/selectors'
import { useSelectorDelegateStore } from '../../../reduxes/stores/root_store/hooks'
import { AudioIndicator } from '../tracks/audio_indicator'
import { useAudioTrack } from '../tracks/use_audio_track'
import { DeviceSelectors } from './device_selectors'

type Props = React.HTMLAttributes<HTMLDivElement> &
  React.ComponentProps<typeof Stack>

export const TrackControls = React.forwardRef<HTMLDivElement, Props>(
  ({ className, ...props }, ref) => {
    const {
      activeCall: { callRecord },
      isJoining,
    } = useCallManager()
    const { localParticipant, localTrackData } = useParticipantManager()
    const topicId = callRecord?.topic_id
    const {
      toggleAudio,
      toggleVideo,
      toggleScreenShare,
      isAudioEnabled,
      isVideoEnabled,
      isScreenShareEnabled,
      isScreenShareSupported,
      unpublishedAudioTrack,
      hasAudioDevices,
      hasVideoDevices,
    } = localTrackData || {}
    const publishedAudioTrack = useAudioTrack(localParticipant)
    const audioTrack = publishedAudioTrack || unpublishedAudioTrack
    const desktop = useMediaQuery(MEDIA_QUERIES.md)
    const setError = useSetError()
    const liveChatApp = useSelectorDelegateStore(isLiveChatApp)

    const toggleA = () => {
      if (toggleAudio) {
        toggleAudio().catch((errorMessage) => {
          setError({ domId: 'mute-btn', error: errorMessage })
        })
      }
    }

    const toggleV = () => {
      if (toggleVideo) {
        toggleVideo().catch((errorMessage) => {
          setError({ domId: 'hide-video-btn', error: errorMessage })
        })
      }
    }

    const toggleSS = () => {
      if (toggleScreenShare) {
        toggleScreenShare().catch((errorMessage) => {
          setError({ domId: 'screen-share-btn', error: errorMessage })
        })
      }
    }

    return (
      <Stack
        as="section"
        gap="2"
        direction="row"
        className={cn(trackControlsStyles(), className)}
        {...props}
        ref={ref}
      >
        {hasAudioDevices && (
          <>
            <Button
              id="mute-btn"
              onClick={toggleA}
              name="audio"
              toggled={isAudioEnabled}
              disabled={isJoining}
              title={isAudioEnabled ? 'Mute Audio' : 'Enable Audio'}
              className={buttonStyles({
                largeVariant: desktop && !liveChatApp,
              })}
              shape="square"
            >
              <AudioIndicator audioTrack={audioTrack} />
              <span>Mic</span>
            </Button>
            <AppErrorMessage domId={'mute-btn'} />
          </>
        )}
        {hasVideoDevices && (
          <>
            <Button
              id="hide-video-btn"
              onClick={toggleV}
              name="video"
              toggled={isVideoEnabled}
              disabled={isJoining}
              title={isVideoEnabled ? 'Hide Video' : 'Show Video'}
              className={buttonStyles({
                largeVariant: desktop && !liveChatApp,
              })}
              shape="square"
            >
              {isVideoEnabled ? <VideoOnIcon /> : <VideoOffIcon />}
              <span>Video</span>
            </Button>
            <AppErrorMessage domId={'hide-video-btn'} />
          </>
        )}
        {isScreenShareSupported && (
          <>
            <Button
              id="screen-share-btn"
              onClick={toggleSS}
              name="screenShare"
              toggled={isScreenShareEnabled}
              disabled={isJoining}
              title={isScreenShareEnabled ? 'Hide Screen' : 'Share Screen'}
              className={buttonStyles({
                largeVariant: desktop && !liveChatApp,
              })}
              shape="square"
            >
              {isScreenShareEnabled ? <ScreenOnIcon /> : <ScreenOffIcon />}
              <span>Screen</span>
            </Button>
            <AppErrorMessage domId={'screen-share-btn'} />
          </>
        )}
        {(!desktop || liveChatApp) && (
          <>
            {liveChatApp && (
              <DeviceSelectors className={trackControlsCallButtonStyles()} />
            )}
            <CallButton
              topicId={topicId}
              shape="square"
              className={trackControlsCallButtonStyles()}
              callType={'audio'}
            />
          </>
        )}
      </Stack>
    )
  },
)

const trackControlsStyles = css({
  position: 'relative',
  width: '100%',
  fontSize: '$1',

  '@md': {
    padding: '0 0 0 $3',
  },
})

export const trackControlsCallButtonStyles = css({
  width: '$space$8 !important',
  height: '$space$8 !important',

  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
})

const buttonStyles = css({
  width: '$space$8 !important',
  height: '$space$8 !important',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '$backgroundSecondary',

  '> svg': {
    stroke: 'none',
  },

  variants: {
    largeVariant: {
      true: {
        flex: 1,
        whiteSpace: 'pre',
        width: '$space$6 !important',
        height: '$space$6 !important',
        flexDirection: 'row',

        '> * + *': {
          marginLeft: '$2',
        },
      },
    },
  },
})

TrackControls.displayName = 'TrackControls'
